import { useCallback } from 'react';

import { getCurrentTemplate } from '@repo/videa-notes';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { endpoints } from '@/react-query/endpoints';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { useSubmitWriteback } from '@/react-query/videa-notes/useSubmitWriteback';
import type { UpdateNotesBody } from '@/react-query/videa-notes/useUpdateNotes';
import { useUpdateNotes } from '@/react-query/videa-notes/useUpdateNotes';

import {
  createNoteUpdatePayload,
  getNoteSummaryWithoutBlurb,
} from '../helpers';
import type { EditedFields } from '../types';

import {
  PrecheckFailedError,
  ProviderSelectionCancelledError,
  useWritebackPrecheck,
} from './useWritebackPrecheck';

export type { ProviderDialogState } from './useWritebackPrecheck';

interface UseSaveNoteToPMSProps {
  currentOrganizationId: string;
  currentPracticeId: string;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  selectedNoteId: string | undefined;
  noteContent: string;
  noteWithTemplateData: ClinicalNote | undefined;
  editedFields: EditedFields;
  hasWritebackEnabled: boolean;
  onWritebackPrecheckFailed?: (reason: string) => void;
  onProviderSelected?: (providerPmsId: string) => void;
  onProviderDialogCancelled?: () => void;
}

/** Single mutation for update + writeback so isPending is true for the whole flow (no gap between the two requests). */
export const useSaveNoteToPMS = ({
  currentOrganizationId,
  currentPracticeId,
  unifiedAppointmentId,
  standalonePatientVisitId,
  selectedNoteId,
  noteContent,
  noteWithTemplateData,
  editedFields,
  hasWritebackEnabled,
  onWritebackPrecheckFailed,
  onProviderSelected,
  onProviderDialogCancelled,
}: UseSaveNoteToPMSProps) => {
  const queryClient = useQueryClient();
  const updateNoteMutation = useUpdateNotes();
  const submitWritebackMutation = useSubmitWriteback();

  const {
    runPrecheck,
    providerDialogState,
    handleProviderConfirm,
    handleProviderCancel,
  } = useWritebackPrecheck({
    onProviderSelected,
    onProviderDialogCancelled,
  });

  const saveToPMSMutation = useMutation({
    mutationFn: async ({
      clinicalNoteId,
      updateNotesBody,
    }: {
      clinicalNoteId: string;
      updateNotesBody: UpdateNotesBody;
    }) => {
      // Step 1: Precheck
      const { providerPmsId } = await runPrecheck(
        clinicalNoteId,
        currentPracticeId,
        currentOrganizationId,
      );

      // Step 2: Update note
      await updateNoteMutation.mutateAsync({
        clinicalNoteId,
        updateNotesBody,
      });

      // Step 3: Submit writeback
      await submitWritebackMutation.mutateAsync({
        clinicalNoteId,
        providerPmsId,
      });

      // Keep isPending true until notes are refetched so UI has updated writebackStatus.
      if (unifiedAppointmentId) {
        await queryClient.refetchQueries({
          queryKey: [
            'videa-notes',
            currentOrganizationId,
            currentPracticeId,
            unifiedAppointmentId,
          ],
        });
      }
      if (standalonePatientVisitId) {
        await queryClient.refetchQueries({
          queryKey: [
            'videa-notes',
            currentOrganizationId,
            currentPracticeId,
            standalonePatientVisitId,
          ],
        });
      }
    },
    onSuccess: () => {
      // Notes list already refetched in mutationFn; only invalidate batch endpoints.
      queryClient.invalidateQueries([
        endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
      ]);
      queryClient.invalidateQueries([
        endpoints.ambient.voiceNotes.batchNotesByStandalonePatientVisitIds,
      ]);
    },
    onError: (error: Error) => {
      // Silent failure for user cancellation
      if (error instanceof ProviderSelectionCancelledError) {
        return;
      }

      if (error instanceof PrecheckFailedError) {
        onWritebackPrecheckFailed?.(error.reason);
      }
    },
  });

  const triggerSaveNoteToPMS = useCallback(() => {
    if (!selectedNoteId) return;

    const noteWithoutBlurb = getNoteSummaryWithoutBlurb(
      noteContent,
      hasWritebackEnabled,
    );

    const currentTemplate = getCurrentTemplate(noteWithTemplateData);
    const isTemplateNote = Boolean(
      currentTemplate && currentTemplate.length > 0,
    );
    const fieldsChanged = editedFields !== null && editedFields.length > 0;

    const updateNotesBody = createNoteUpdatePayload(
      noteWithoutBlurb,
      isTemplateNote,
      fieldsChanged,
      noteWithTemplateData,
      editedFields,
    );

    saveToPMSMutation.mutate({
      clinicalNoteId: selectedNoteId,
      updateNotesBody,
    });
  }, [
    selectedNoteId,
    noteContent,
    noteWithTemplateData,
    editedFields,
    hasWritebackEnabled,
    saveToPMSMutation,
  ]);

  return {
    triggerSaveNoteToPMS,
    isSavingNoteToPMS: saveToPMSMutation.isPending,
    providerDialogState,
    handleProviderConfirm,
    handleProviderCancel,
  };
};
